import { useState, useEffect } from 'react'
import { Trash2 } from 'lucide-react'
import { useThemeColors } from '../hooks/useThemeColors'
import { useIsMobile } from '../hooks/useIsMobile'
import { usePrimateAuth } from '../hooks/usePrimateAuth'
import { getAdminWaitlist, adminInvite, adminDeleteWaitlistEntry, type WaitlistEntry } from '../lib/api'

type FilterTab = 'all' | 'pending' | 'invited' | 'accepted'

// Sortable columns. Each maps to a value extractor used by the comparator.
type SortKey =
  | 'email' | 'full_name' | 'company' | 'vertical' | 'status'
  | 'created_at' | 'invited_at' | 'activated' | 'activated_at'
type SortDir = 'asc' | 'desc'

function sortValue(e: WaitlistEntry, key: SortKey): string | number {
  switch (key) {
    case 'vertical':
      return ((e as unknown as { vertical?: string }).vertical || e.use_case || '').toLowerCase()
    case 'activated':
      return e.activated ? 1 : 0
    case 'email':
    case 'full_name':
    case 'company':
    case 'status':
      return ((e[key] as string) || '').toLowerCase()
    case 'created_at':
    case 'invited_at':
    case 'activated_at': {
      // Nullable dates: missing sorts last in asc (treated as +Infinity so it
      // lands at the bottom regardless of direction toggle expectations).
      const v = e[key] as string | null | undefined
      return v ? Date.parse(v) : Number.NEGATIVE_INFINITY
    }
    default:
      return ''
  }
}

export default function AdminWaitlist() {
  // Gate via usePrimateAuth (single source of truth for admin/auth state). This
  // honors the primate-dev-admin bypass used by tests + the screenshot harness,
  // and matches how the rest of the app (nav, sub-sheets) decides admin. The
  // email allowlist still lives in PrimateAuthContext, so production behavior is
  // unchanged — only the binding moves off Clerk's useUser() directly. (2026-06-11)
  const { isAdmin, authLoaded } = usePrimateAuth()
  const c = useThemeColors()
  const isMobile = useIsMobile()
  const [entries, setEntries] = useState<WaitlistEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<FilterTab>('all')
  // Default sort: newest signups first (matches the prior implicit order).
  const [sortKey, setSortKey] = useState<SortKey>('created_at')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      // Dates + activated default to desc (most recent / activated first);
      // text columns default to asc (A→Z).
      setSortDir(['created_at', 'invited_at', 'activated_at', 'activated'].includes(key) ? 'desc' : 'asc')
    }
  }
  useEffect(() => {
    if (isAdmin) loadEntries()
  }, [isAdmin])

  async function loadEntries() {
    setLoading(true)
    try {
      const data = await getAdminWaitlist()
      setEntries(data.entries)
    } finally {
      setLoading(false)
    }
  }

  const [inviting, setInviting] = useState<string | null>(null)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null)
  const [scrubbing, setScrubbing] = useState<string | null>(null)

  async function handleInvite(id: string) {
    setInviting(id)
    setInviteError(null)
    setInviteSuccess(null)
    try {
      const result = await adminInvite(id)
      setEntries(prev =>
        prev.map(e => (e.id === id ? { ...e, status: 'invited', invited_at: result.invited_at } : e))
      )
      const entry = entries.find(e => e.id === id)
      setInviteSuccess(`Invite sent to ${entry?.email ?? 'user'}`)
      setTimeout(() => setInviteSuccess(null), 4000)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to send invite'
      setInviteError(msg)
      setTimeout(() => setInviteError(null), 5000)
    } finally {
      setInviting(null)
    }
  }

  async function handleScrub(entry: WaitlistEntry) {
    const confirmed = window.confirm(
      `Delete ${entry.email} from the waitlist and scrub their account, history, videos, and artifacts from this environment? This cannot be undone.`
    )
    if (!confirmed) return

    setScrubbing(entry.id)
    setInviteError(null)
    setInviteSuccess(null)
    try {
      const result = await adminDeleteWaitlistEntry(entry.id)
      setEntries(prev => prev.filter(e => e.id !== entry.id))
      setInviteSuccess(`Scrubbed ${result.email}`)
      setTimeout(() => setInviteSuccess(null), 4000)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to scrub user'
      setInviteError(msg)
      setTimeout(() => setInviteError(null), 5000)
    } finally {
      setScrubbing(null)
    }
  }

  if (!authLoaded) return <div className="min-h-screen bg-void" />

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-void text-chalk flex items-center justify-center">
        <p className="text-body text-steel">Access denied</p>
      </div>
    )
  }

  const filteredEntries =
    filter === 'all' ? entries : entries.filter(e => e.status === filter)

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    const av = sortValue(a, sortKey)
    const bv = sortValue(b, sortKey)
    let cmp = 0
    if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv
    else cmp = String(av).localeCompare(String(bv))
    return sortDir === 'asc' ? cmp : -cmp
  })

  // Clickable sortable header. Shows ▲/▼ on the active column.
  function SortableTh({ label, sortKey: key, last = false, title }: { label: string; sortKey: SortKey; last?: boolean; title?: string }) {
    const active = sortKey === key
    return (
      <th
        title={title}
        onClick={() => toggleSort(key)}
        style={{
          textAlign: 'left',
          padding: last ? '0 0 10px 0' : '0 16px 10px 0',
          fontWeight: 500,
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          color: active ? c.signal : c.textMuted,
        }}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        data-testid={`sort-${key}`}
      >
        {label}
        <span style={{ marginLeft: 4, fontSize: '10px', opacity: active ? 1 : 0.35 }}>
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </th>
    )
  }

  function statusBadge(status: string) {
    const classes: Record<string, string> = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      invited: 'bg-signal/20 text-signal',
      accepted: 'bg-green-500/20 text-green-400',
      signed_up: 'bg-signal/20 text-signal',
    }
    return (
      <span className={`px-2 py-0.5 rounded text-footnote font-medium ${classes[status] ?? 'bg-carbon text-steel'}`}>
        {status}
      </span>
    )
  }

  function ScrubButton({ entry, mobile = false }: { entry: WaitlistEntry; mobile?: boolean }) {
    const busy = scrubbing === entry.id
    return (
      <button
        onClick={() => !busy ? handleScrub(entry) : undefined}
        disabled={busy}
        aria-label={`Scrub ${entry.email}`}
        title="Delete and scrub user data"
        data-testid={mobile ? `card-scrub-${entry.id}` : `row-scrub-${entry.id}`}
        style={{
          width: mobile ? 38 : 28,
          height: mobile ? 38 : 28,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${busy ? c.border : 'rgba(248,113,113,0.35)'}`,
          borderRadius: mobile ? 9 : 7,
          background: busy ? c.bgSurface : 'rgba(220,38,38,0.08)',
          color: busy ? c.textMuted : '#F87171',
          cursor: busy ? 'not-allowed' : 'pointer',
          opacity: busy ? 0.55 : 1,
          padding: 0,
          flexShrink: 0,
        }}
      >
        <Trash2 size={mobile ? 15 : 14} aria-hidden="true" />
      </button>
    )
  }

  // Mobile sort control — cards have no column headers, so expose sort here.
  const SORT_OPTIONS: Array<{ key: SortKey; label: string }> = [
    { key: 'created_at', label: 'Signed up' },
    { key: 'invited_at', label: 'Date invited' },
    { key: 'activated', label: 'Activated' },
    { key: 'activated_at', label: 'Date activated' },
    { key: 'full_name', label: 'Name' },
    { key: 'company', label: 'Company' },
    { key: 'status', label: 'Status' },
    { key: 'email', label: 'Email' },
  ]
  function MobileSortControl() {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <select
          data-testid="mobile-sort-select"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as SortKey)}
          style={{
            flex: 1, padding: '10px 12px', borderRadius: 10,
            border: `1px solid ${c.border}`, background: c.bgSurface,
            color: c.textPrimary, fontSize: 14, fontFamily: 'inherit',
            WebkitAppearance: 'none', appearance: 'none',
          }}
        >
          {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>Sort: {o.label}</option>)}
        </select>
        <button
          data-testid="mobile-sort-dir"
          onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
          aria-label={sortDir === 'asc' ? 'Ascending' : 'Descending'}
          style={{
            minWidth: 44, minHeight: 44, borderRadius: 10,
            border: `1px solid ${c.border}`, background: c.bgSurface,
            color: c.signal, fontSize: 16, cursor: 'pointer',
          }}
        >{sortDir === 'asc' ? '↑' : '↓'}</button>
      </div>
    )
  }

  // Mobile-native stacked card for one waitlist entry.
  function WaitlistCard({ entry, rowNumber }: { entry: WaitlistEntry; rowNumber: number }) {
    const vertical = (entry as unknown as { vertical?: string }).vertical || entry.use_case || ''
    const initials = (entry.full_name || entry.email).slice(0, 2).toUpperCase()
    return (
      <div
        data-testid="waitlist-card"
        style={{
          border: `1px solid ${c.border}`, borderRadius: 14, padding: 14,
          marginBottom: 12, background: c.bgSurface,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            aria-label={`Row ${rowNumber}`}
            data-testid={`card-row-number-${entry.id}`}
            style={{
              minWidth: 24,
              height: 24,
              borderRadius: 8,
              border: `1px solid ${c.border}`,
              color: c.textMuted,
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {rowNumber}
          </div>
          <div style={{
            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
            background: entry.activated ? '#16794a' : (entry.status === 'invited' ? c.signal : c.textMuted),
            color: '#fff', fontSize: 12, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {entry.full_name || <span style={{ color: c.textMuted }}>—</span>}
            </div>
            <div style={{ fontSize: 12, color: c.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {entry.email}{entry.company ? ` · ${entry.company}` : ''}
            </div>
          </div>
          {statusBadge(entry.status)}
        </div>
        {vertical && (
          <div style={{ fontSize: 12, color: c.textMuted, marginTop: 8, paddingLeft: 68, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vertical}</div>
        )}
        {/* The three requested columns, as a labelled mini-grid */}
        <div style={{ display: 'flex', gap: 18, marginTop: 10, paddingLeft: 68, flexWrap: 'wrap' }}>
          <CardStat label="Invited" value={entry.invited_at ? entry.invited_at.split('T')[0] : '—'} />
          <CardStat
            label="Activated"
            value={entry.activated ? 'Yes ✓' : '—'}
            valueColor={entry.activated ? '#16794a' : c.textMuted}
          />
          <CardStat label="Activated on" value={entry.activated_at ? entry.activated_at.split('T')[0] : '—'} />
        </div>
        <div style={{ marginTop: 12, paddingLeft: 68, display: 'flex', alignItems: 'center', gap: 10 }}>
          {entry.status === 'pending' && (
            <button
              onClick={() => !inviting ? handleInvite(entry.id) : undefined}
              disabled={inviting === entry.id}
              data-testid={`card-invite-${entry.id}`}
              style={{
                color: '#fff', background: c.signal, border: 'none', borderRadius: 9,
                padding: '8px 14px', fontSize: 13, fontWeight: 600,
                cursor: inviting === entry.id ? 'not-allowed' : 'pointer',
                opacity: inviting === entry.id ? 0.5 : 1,
              }}
            >{inviting === entry.id ? 'Sending…' : 'Invite →'}</button>
          )}
          <ScrubButton entry={entry} mobile />
        </div>
      </div>
    )
  }

  function CardStat({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
    return (
      <div>
        <div style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.textMuted, opacity: 0.8 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: valueColor ?? c.textPrimary, marginTop: 1 }}>{value}</div>
      </div>
    )
  }

  return (
    <div className={`flex-1 text-chalk ${isMobile ? 'p-4' : 'p-8'}`}>
      <div style={{ maxWidth: "960px" }}>
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-h1 text-chalk">Waitlist</h1>
          <button onClick={loadEntries} className="text-body-sm text-signal hover:underline">
            Refresh
          </button>
        </div>

        {inviteSuccess && (
          <div style={{ background: 'rgba(22,163,74,0.12)', border: '1px solid rgba(22,163,74,0.35)', borderRadius: '3px', padding: '12px 16px', marginBottom: '16px', color: '#4ADE80', fontSize: '14px' }}>
            ✓ {inviteSuccess}
          </div>
        )}
        {inviteError && (
          <div style={{ background: 'rgba(220,38,38,0.10)', border: '1px solid rgba(220,38,38,0.30)', borderRadius: '3px', padding: '12px 16px', marginBottom: '16px', color: '#F87171', fontSize: '14px' }}>
            ✗ {inviteError}
          </div>
        )}
        <div className="flex gap-4 mb-6">
          {(['all', 'pending', 'invited', 'accepted'] as FilterTab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`text-body-sm capitalize px-3 py-1 rounded ${
                filter === tab ? 'bg-signal text-white' : 'text-steel hover:text-chalk'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {loading ? (
          <div
            data-testid="loading-spinner"
            className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-signal mx-auto"
          />
        ) : isMobile ? (
          // Mobile-native: sort control + stacked cards (no horizontal-scroll table).
          <div data-testid="waitlist-cards">
            <MobileSortControl />
            {sortedEntries.length === 0 ? (
              <div style={{ color: c.textMuted, fontSize: 14, padding: '24px 0', textAlign: 'center' }}>No entries.</div>
            ) : (
              sortedEntries.map((entry, index) => <WaitlistCard key={entry.id} entry={entry} rowNumber={index + 1} />)
            )}
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <colgroup>
              <col style={{ width: '44px' }} />{/* Visible row number for the current filtered/sorted view */}
              <col style={{ width: '220px' }} />{/* Email */}
              <col style={{ width: '130px' }} />{/* Name */}
              <col style={{ width: '130px' }} />{/* Company */}
              <col style={{ width: '230px' }} />{/* Vertical — widest option: "Smart Cameras / Physical Security" */}
              <col style={{ width: '80px' }} />{/* Status */}
              <col style={{ width: '90px' }} />{/* Created */}
              <col style={{ width: '90px' }} />{/* Date invited */}
              <col style={{ width: '90px' }} />{/* Activated */}
              <col style={{ width: '90px' }} />{/* Activated on */}
              <col style={{ width: '88px' }} />{/* Actions */}
            </colgroup>
            <thead>
              <tr style={{ borderBottom: `1px solid ${c.carbon}`, color: c.textMuted }}>
                <th style={{ textAlign: 'left', padding: '0 12px 10px 0', fontWeight: 500, color: c.textMuted }}>#</th>
                <SortableTh label="Email" sortKey="email" />
                <SortableTh label="Name" sortKey="full_name" />
                <SortableTh label="Company" sortKey="company" />
                <SortableTh label="Vertical" sortKey="vertical" />
                <SortableTh label="Status" sortKey="status" />
                <SortableTh label="Signed up" sortKey="created_at" />
                <SortableTh label="Invited" sortKey="invited_at" />
                <SortableTh label="Activated" sortKey="activated" title="Accepted invite, completed onboarding, and submitted a real prompt" />
                <SortableTh label="Activated on" sortKey="activated_at" />
                <th style={{ textAlign: 'left', padding: '0 0 10px 0', fontWeight: 500 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map((entry, index) => (
                <tr key={entry.id} style={{ borderBottom: `1px solid ${c.border}` }}>
                  <td
                    aria-label={`Row ${index + 1}`}
                    data-testid={`row-number-${entry.id}`}
                    style={{ padding: '10px 12px 10px 0', color: c.textMuted, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}
                  >
                    {index + 1}
                  </td>
                  <td style={{ padding: '10px 16px 10px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '220px', color: c.textPrimary }}>
                    {entry.email}
                  </td>
                  <td style={{ padding: '10px 16px 10px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: c.textPrimary }}>
                    {entry.full_name || <span style={{ color: c.textMuted }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 16px 10px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: c.textMuted }}>
                    {entry.company || <span style={{ color: c.textMuted }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 16px 10px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: c.textMuted, maxWidth: '230px' }}>
                    {(entry as unknown as {vertical?: string}).vertical || entry.use_case || <span style={{ color: c.textMuted }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 16px 10px 0' }}>
                    {statusBadge(entry.status)}
                    {/* PRI-387: an accepted entry with no account is a dead-end invite -
                        flag it so 'accepted' isn't mistaken for a real success. */}
                    {entry.status === 'accepted' && entry.account_created === false && (
                      <span style={{ marginLeft: 6, color: c.textMuted, fontSize: 11 }}>
                        no account yet
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 16px 10px 0', color: c.textMuted, whiteSpace: 'nowrap' }}>
                    {entry.created_at.split('T')[0]}
                  </td>
                  {/* Date invited */}
                  <td style={{ padding: '10px 16px 10px 0', color: c.textMuted, whiteSpace: 'nowrap' }}>
                    {entry.invited_at ? entry.invited_at.split('T')[0] : <span style={{ color: c.textMuted }}>—</span>}
                  </td>
                  {/* Activated: accepted invite + onboarded + submitted a real prompt */}
                  <td style={{ padding: '10px 16px 10px 0', whiteSpace: 'nowrap' }}>
                    {entry.activated
                      ? <span style={{ color: c.signal, fontWeight: 600 }}>Yes</span>
                      : <span style={{ color: c.textMuted }}>—</span>}
                  </td>
                  {/* Date activated */}
                  <td style={{ padding: '10px 16px 10px 0', color: c.textMuted, whiteSpace: 'nowrap' }}>
                    {entry.activated_at ? entry.activated_at.split('T')[0] : <span style={{ color: c.textMuted }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {entry.status === 'pending' && (
                        <button
                          onClick={() => !inviting ? handleInvite(entry.id) : undefined}
                          disabled={inviting === entry.id}
                          style={{ color: c.signal, background: 'none', border: 'none', cursor: inviting === entry.id ? 'not-allowed' : 'pointer', fontSize: '13px', padding: 0, opacity: inviting === entry.id ? 0.5 : 1, whiteSpace: 'nowrap' }}
                        >
                          {inviting === entry.id ? 'Sending…' : 'Invite →'}
                        </button>
                      )}
                      {entry.status === 'invited' && (
                        <span style={{ color: c.textMuted, fontSize: '12px' }}>Invited</span>
                      )}
                      <ScrubButton entry={entry} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
